import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Copy, Wallet, TrendingUp, Clock, CheckCircle2, MapPin, MousePointerClick, Megaphone } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useAuth } from '@/contexts/AuthContext';
import { naira, StatusPill, BankForm, PayoutRequest } from '@/components/affiliate/AffiliateWidgets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ icon: Icon, label, value, tint, testId }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-3xl border border-health-line p-5 shadow-soft">
    <span className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${tint}`}><Icon className="h-5 w-5" /></span>
    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
    <p className="font-heading text-2xl font-bold text-health-ink mt-1" data-testid={testId}>{value}</p>
  </motion.div>
);

const Table = ({ headers, rows, empty, testId }) => (
  <div className="overflow-x-auto" data-testid={testId}>
    {rows.length === 0 ? <p className="text-sm text-slate-500 py-6 text-center">{empty}</p> : (
      <table className="w-full text-sm">
        <thead><tr className="text-left text-slate-500 border-b border-health-line">{headers.map((h) => <th key={h} className="py-2 pr-4 font-semibold">{h}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i} className="border-b border-health-line/60 last:border-0">{r.map((c, j) => <td key={j} className="py-3 pr-4 whitespace-nowrap">{c}</td>)}</tr>)}</tbody>
      </table>
    )}
  </div>
);

const Card = ({ title, children, className = '' }) => (
  <div className={`bg-white rounded-3xl border border-health-line p-6 shadow-soft ${className}`}>
    <h2 className="font-heading text-lg font-bold text-health-ink mb-4">{title}</h2>
    {children}
  </div>
);

export default function AffiliateDashboard() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [banks, setBanks] = useState([]);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [me, b] = await Promise.all([axios.get(`${API}/affiliate/me`, { headers }), axios.get(`${API}/payouts/banks`, { headers })]);
      setData(me.data);
      setBanks(b.data.banks);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load your dashboard');
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const copy = (text) => {
    Promise.resolve(navigator.clipboard?.writeText(text))
      .then(() => toast.success('Copied!'))
      .catch(() => toast.error('Could not copy - please copy it manually'));
  };

  if (error) return (<div className="min-h-screen bg-background"><Navbar /><div className="pt-40 text-center text-slate-600" data-testid="affiliate-error">{error}</div></div>);
  if (!data) return (<div className="min-h-screen bg-background"><Navbar /><div className="pt-48 flex justify-center"><div className="spinner" /></div></div>);

  const { affiliate, stats, states, commissions, payouts, referral_link } = data;
  return (
    <div className="min-h-screen bg-background font-body text-health-ink">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-28 pb-16 space-y-8" data-testid="affiliate-dashboard">
        <div className="relative overflow-hidden rounded-[2rem] bg-health-emerald text-white p-7 md:p-10">
          <Megaphone className="absolute -right-6 -bottom-6 h-40 w-40 text-white/10" />
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-100">Brand ambassador</p>
          <h1 className="font-heading text-3xl md:text-4xl font-bold mt-2">Hi {affiliate.full_name.split(' ')[0]}, keep sharing the care!</h1>
          <p className="text-emerald-50 mt-2">You earn <b>{affiliate.commission_rate}%</b> on every delivered order you refer.</p>
          <div className="mt-6 flex flex-col md:flex-row gap-3">
            <button onClick={() => copy(referral_link)} className="flex items-center justify-between gap-3 bg-white/15 hover:bg-white/25 rounded-2xl px-4 py-3 text-left transition-colors" data-testid="copy-referral-link">
              <span className="truncate text-sm">{referral_link}</span><Copy className="h-4 w-4 shrink-0" />
            </button>
            <button onClick={() => copy(affiliate.code)} className="flex items-center gap-3 bg-white text-health-emerald rounded-2xl px-5 py-3 font-heading font-bold" data-testid="copy-referral-code">
              Code: {affiliate.code} <Copy className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          <Stat icon={TrendingUp} label="Referred sales" value={naira(stats.sales_total)} tint="bg-health-sky text-sky-700" testId="aff-stat-sales" />
          <Stat icon={CheckCircle2} label="Orders" value={stats.orders_count} tint="bg-health-mint text-health-emerald" testId="aff-stat-orders" />
          <Stat icon={Clock} label="Pending" value={naira(stats.pending_commission + stats.awaiting_approval_commission)} tint="bg-amber-100 text-amber-700" testId="aff-stat-pending" />
          <Stat icon={Wallet} label="Earned" value={naira(stats.earned_total)} tint="bg-health-mint text-health-emerald" testId="aff-stat-earned" />
          <Stat icon={CheckCircle2} label="Paid out" value={naira(stats.paid_out)} tint="bg-health-peach text-health-coral" testId="aff-stat-paid" />
          <Stat icon={MousePointerClick} label="Link clicks" value={stats.clicks} tint="bg-violet-100 text-violet-700" testId="aff-stat-clicks" />
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <Card title="Withdraw earnings">
            <p className="text-sm text-slate-500">Available balance</p>
            <p className="font-heading text-3xl font-bold text-health-emerald mb-1" data-testid="aff-available-balance">{naira(stats.available_balance)}</p>
            {stats.in_progress_payouts > 0 && <p className="text-xs text-slate-500 mb-3">{naira(stats.in_progress_payouts)} in progress</p>}
            {affiliate.bank ? <PayoutRequest token={token} available={stats.available_balance} onDone={load} /> : <p className="text-sm text-health-coral">Add a payout account first.</p>}
          </Card>
          <Card title="Payout account">
            {affiliate.bank && (
              <div className="mb-4 p-3 rounded-2xl bg-health-mint/60 text-sm" data-testid="aff-bank-current">
                <p className="font-semibold">{affiliate.bank.account_name}</p>
                <p className="text-slate-600">{affiliate.bank.bank_name} · ******{affiliate.bank.account_number.slice(-4)} {affiliate.bank.verified && <span className="text-health-emerald font-bold">✓ verified</span>}</p>
              </div>
            )}
            <BankForm token={token} banks={banks} current={affiliate.bank} onSaved={load} />
          </Card>
          <Card title="Customers by delivery state">
            <Table testId="aff-states-table" headers={['State', 'Orders', 'Sales']} empty="No referred orders yet"
              rows={states.map((s) => [<span className="flex items-center gap-1"><MapPin className="h-3 w-3 text-health-coral" />{s.state}</span>, s.orders, naira(s.sales)])} />
          </Card>
        </div>

        <Card title="Commissions">
          <Table testId="aff-commissions-table" headers={['Date', 'Order', 'Order value', 'Rate', 'Commission', 'State', 'Status']} empty="Share your link to start earning!"
            rows={commissions.map((c) => [new Date(c.created_at).toLocaleDateString(), `#${c.order_id.slice(0, 8)}`, naira(c.base_amount), `${c.rate}%`, naira(c.amount), c.delivery_state || '-', <StatusPill status={c.status} />])} />
        </Card>
        <Card title="Payout history">
          <Table testId="aff-payouts-table" headers={['Date', 'Amount', 'Bank', 'Status', 'Note']} empty="No payouts yet"
            rows={payouts.map((p) => [new Date(p.created_at).toLocaleDateString(), naira(p.amount), `${p.destination?.bank_name || ''} ****${(p.destination?.account_number || '').slice(-4)}`, <StatusPill status={p.status} />, p.failure_reason || '-'])} />
        </Card>
      </div>
      <Footer />
    </div>
  );
}
